<template>
  <OverlayContainer
    v-show="show"
    @close="close"
  >
    <template v-slot:header>
      {{ $t('ui.envelopes.create.steps.documents.selectTemplatesTitle') }}
    </template>
    <template v-slot:default>
      <TemplateSelectorScreen
        v-if="show"
        class="p-base lg:px-xl pr-0 flex-grow"
        :templates="templates"
        :pendingTemplates="isPendingTemplates"
        :fetchingTemplates="isFetchingTemplates"
        :fetchingNextTemplates="isFetchingNextTemplates"
        :placeholderTemplates="isPlaceholderTemplates"
        :totalTemplates="totalTemplates"
        :hasMore="hasNextTemplates"
        :selectedTemplates="temporarySelectedTemplates"
        @search="searchTemplates"
        @next="fetchNextTemplates"
        @add="add"
        @remove="remove"
        @goBack="close"
        listClass="h-0 min-h-full overflow-y-auto pr-base pb-lg lg:pr-xl lg:-mr-xl"
        ec="documents_screen"
      />
    </template>

    <template v-slot:footer>
      <div class="float-right">
        <BaseButton
          variant="invisible"
          @click.native="close"
          data-ec="create_envelope_template_selector"
          data-et="cancel_button"
        >
          {{ $t('ui.envelopes.create.steps.documents.cancelSelection') }}
        </BaseButton>

        <BaseButton
          @click.native="confirm"
          data-ec="create_envelope_template_selector"
          data-et="confirm_button"
        >
          {{ $t('ui.envelopes.create.steps.documents.confirmSelection') }}
        </BaseButton>
      </div>
    </template>
  </OverlayContainer>
</template>

<script setup>
// @ts-check
import { computed, defineExpose, defineProps, ref } from 'vue';
import { useReactiveMap, useReactiveSet } from 'vue-reactive-collection';

import { useInfiniteTemplates } from '@/composables/templates';

import BaseButton from '@/components/library/buttons/BaseButton.vue';
import { OverlayContainer } from '@/components/library/containers/OverlayContainer';
import TemplateSelectorScreen from '@/components/screens/envelopeCreate/TemplateSelectorScreen.vue';

/** @typedef {import('@/components/screens/envelopeCreate/TemplateSelectorScreen.vue').TTemplate} TTemplate */
/** @typedef {import('@/store/modules/bulkSend/models/Templates').TListTemplate} TListTemplate */

const props = defineProps({
  /** @type {import('vue').PropOptions<TTemplate[]>} */
  selectedTemplates: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['addTemplates', 'removeTemplates']);

const show = ref(false);

const query = ref('');
const {
  templates,
  fetchNextTemplates,
  hasNextTemplates,
  totalTemplates,
  isPendingTemplates,
  isFetchingTemplates,
  isFetchingNextTemplates,
  isPlaceholderTemplates,
} = useInfiniteTemplates(query, show);

/** @param {string} value */
function searchTemplates(value) {
  query.value = value;
}

/** @type {import('vue').Ref<Map<string,TTemplate>>} */
const selected = useReactiveMap();
/** @type {import('vue').Ref<Set<string>>} */
const deselected = useReactiveSet();

const temporarySelectedTemplates = computed(() =>
  [...selected.value.values(), ...(props.selectedTemplates || [])]
    .filter(template => !deselected.value.has(template.fingerprint)));

function open() {
  show.value = true;
}

function close() {
  selected.value.clear();
  deselected.value.clear();
  query.value = '';

  show.value = false;
}

function confirm() {
  emit('addTemplates', selected.value);
  emit('removeTemplates', deselected.value);

  close();
}

/**
 * @param {string} fingerprint
 * @param {string} title
 */
function add(fingerprint, title) {
  if (!deselected.value.delete(fingerprint)) {
    selected.value.set(fingerprint, { fingerprint, title });
  }
}

/** @param {string} fingerprint */
function remove(fingerprint) {
  if (!selected.value.delete(fingerprint)) {
    deselected.value.add(fingerprint);
  }
}

defineExpose({ open });
</script>
